import { FC } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import { observer } from 'mobx-react-lite';

import { ListPagination } from '@components';
import { type SingularKeys, Trans, useTrans } from '@libs/i18n';

import type { TenantProfileDocumentDto } from '../../api/types';

import { DocumentTableRow } from './DocumentTableRow';

interface Props {
  /** The current page of documents (already paginated by the backend). */
  docs: TenantProfileDocumentDto[];
  /** Resolves a category key to its localized name (null when unknown). */
  categoryName: (categoryKey: string | null) => string | null;
  onReview: (id: string) => void;
  onExtract: (id: string) => void;
  onRetryClassify: (id: string) => void;
  onDelete: (id: string) => void;
  /** Current 1-based page, total pages and overall count from the backend. */
  page: number;
  totalPages: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  /** Dims the pager while a page request is in flight. */
  isLoading?: boolean;
}

const HEADERS: Array<{ key: SingularKeys; align?: 'right' }> = [
  { key: 'app.documentHub.table.document' },
  { key: 'app.documentHub.table.category' },
  { key: 'app.documentHub.table.status' },
  { key: 'app.documentHub.table.progress' },
  { key: 'app.documentHub.table.uploaded' },
  { key: 'app.documentHub.table.actions', align: 'right' },
];

/**
 * The documents data table. Matches the assessments table styling: a Paper
 * surface with a rounded border, a sticky grey header with uppercase caption
 * labels, and horizontal scroll on narrow viewports.
 */
export const DocumentsTable: FC<Props> = observer(
  ({
    docs,
    categoryName,
    onReview,
    onExtract,
    onRetryClassify,
    onDelete,
    page,
    totalPages,
    totalCount,
    onPageChange,
    isLoading,
  }) => {
    const theme = useTheme();
    const { t } = useTrans();

    return (
      <Paper
        sx={{
          // Fixed height (sized for a full page of rows) so the card is the
          // same size on every page — even with a single document — and the
          // pagination footer always sits in the same place.
          height: 640,
          minWidth: 0,
          borderRadius: 4,
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.grey[300]}`,
          boxShadow: 'none',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <TableContainer
          sx={{
            // Grow to fill the card so the pagination footer is pushed to the
            // bottom edge.
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            overflow: 'auto',
          }}
        >
          <Table sx={{ width: '100%', minWidth: 1080, tableLayout: 'fixed' }} stickyHeader>
            <colgroup>
              <col style={{ width: '26%' }} />
              <col style={{ width: '15%' }} />
              <col style={{ width: '13%' }} />
              <col style={{ width: '16%' }} />
              <col style={{ width: '15%' }} />
              <col style={{ width: '15%' }} />
            </colgroup>
            <TableHead>
              <TableRow>
                {HEADERS.map((h, idx) => (
                  <TableCell
                    key={String(h.key)}
                    align={h.align}
                    sx={{
                      backgroundColor: theme.palette.grey[200],
                      paddingY: 1.5,
                      paddingLeft: idx === 0 ? 2 : 1,
                      paddingRight: idx === HEADERS.length - 1 ? 3 : 1,
                      borderBottom: `1px solid ${theme.palette.grey[300]}`,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: theme.palette.text.secondary,
                        letterSpacing: '0.4px',
                        textTransform: 'uppercase',
                      }}
                    >
                      {t(h.key)}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {docs.map((doc) => (
                <DocumentTableRow
                  key={doc.id}
                  doc={doc}
                  categoryName={categoryName(doc.categoryKey)}
                  onReview={onReview}
                  onExtract={onExtract}
                  onRetryClassify={onRetryClassify}
                  onDelete={onDelete}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {totalCount > 0 && (
          <Box
            sx={{
              px: 3,
              pb: 3,
              pt: 1.5,
              borderTop: `1px solid ${theme.palette.grey[300]}`,
              backgroundColor: theme.palette.background.paper,
            }}
          >
            <ListPagination
              page={page}
              totalPages={totalPages}
              onPageChange={onPageChange}
              isLoading={isLoading}
              sx={{ mt: 0 }}
              summary={
                <Trans
                  i18nKey="app.documentHub.pagination.summary"
                  values={{ page, totalPages, totalCount }}
                />
              }
            />
          </Box>
        )}
      </Paper>
    );
  },
);
